import { CheckCircle2Icon, FilterIcon, XCircleIcon, XIcon } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import type { ColumnDef } from "@blis/insights/entities/types";
import {
  defaultExploreFilterState,
  type ExploreFilterState,
  operatorNeedsValue,
  operatorsForColumnType,
  upsertColumnFilter,
} from "@blis/insights/explore/explore-filter-state";
import type { Filter, FilterOp } from "@blis/insights/explore/types";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Popover } from "@/components/ui/popover";
import { hasActiveExploreFilters } from "../explore/describeExploreFilters";

type MenuPage =
  | "root"
  | `field:${string}`
  | `operators:${string}`
  | `value:${string}:${FilterOp}`
  | `enum:${string}`;

export function ExploreFilterMenu({
  state,
  columns,
  defaultDateField,
  onChange,
}: {
  state: ExploreFilterState;
  columns: readonly ColumnDef[];
  defaultDateField: string;
  onChange: (next: ExploreFilterState) => void;
}) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<MenuPage>("root");
  const [search, setSearch] = useState("");
  const [draftValue, setDraftValue] = useState("");

  const filterableColumns = useMemo(
    () =>
      columns
        .filter((c) => !c.phi && c.field !== defaultDateField)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [columns, defaultDateField],
  );

  const preferredFields = useMemo(
    () =>
      new Set([
        "test_type",
        "test_spec",
        "accession_priority",
        "client_org",
        "final_out_of_range",
        "is_validated",
        "is_reflex",
      ]),
    [],
  );

  const visibleColumns = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = filterableColumns.filter((column) => {
      if (!query) return true;
      return (
        column.label.toLowerCase().includes(query) ||
        column.field.toLowerCase().includes(query)
      );
    });

    return [...filtered].sort((a, b) => {
      const aPreferred = preferredFields.has(a.field) ? 0 : 1;
      const bPreferred = preferredFields.has(b.field) ? 0 : 1;
      if (aPreferred !== bPreferred) return aPreferred - bPreferred;
      return a.label.localeCompare(b.label);
    });
  }, [filterableColumns, preferredFields, search]);

  const activeField =
    page.startsWith("field:") ||
    page.startsWith("operators:") ||
    page.startsWith("value:") ||
    page.startsWith("enum:")
      ? page.split(":")[1]
      : null;

  const activeColumn = activeField
    ? columns.find((c) => c.field === activeField)
    : null;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset search when drill-down page changes
  useEffect(() => {
    setSearch("");
    setDraftValue("");
  }, [page]);

  const onOpenChange = (flag: boolean) => {
    if (!flag) {
      setTimeout(() => setPage("root"), 200);
    }
    setSearch("");
    setDraftValue("");
    setOpen(flag);
  };

  const applyColumnFilter = (filter: Filter) => {
    onChange(upsertColumnFilter(state, filter));
    onOpenChange(false);
  };

  const toggleEnumValue = (field: string, value: string, checked: boolean) => {
    const existing = state.columnFilters.find((f) => f.field === field);
    const current =
      existing?.op === "in" && Array.isArray(existing.value)
        ? (existing.value as string[])
        : [];

    const nextValues = checked
      ? [...current, value]
      : current.filter((v) => v !== value);

    if (nextValues.length === 0) {
      onChange({
        ...state,
        columnFilters: state.columnFilters.filter((f) => f.field !== field),
      });
      return;
    }

    onChange(
      upsertColumnFilter(state, {
        field,
        op: "in",
        value: nextValues,
      }),
    );
  };

  const hasFilters = hasActiveExploreFilters(state);

  return (
    <DialogTrigger isOpen={open} onOpenChange={onOpenChange}>
      <Button
        size="sm"
        variant={hasFilters ? "default" : "outline"}
        onPress={() => setOpen(true)}
      >
        <FilterIcon data-slot="icon" />
        Filter
      </Button>
      <Popover
        className="p-0 sm:min-w-[240px] sm:max-w-[320px]"
        placement="bottom start"
      >
        <Dialog
          className="p-0! outline-hidden"
          aria-label="Filter explore data"
          hideCloseButton
        >
          <Command
            className="**:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-2 [&_[cmdk-item]>svg]:size-4 [&_[cmdk-item]>svg]:mr-2"
            onKeyDown={(e) => {
              if (e.key === "Escape" && page !== "root") {
                e.preventDefault();
                e.stopPropagation();
                setPage("root");
                setSearch("");
              }
              if (e.key.startsWith("Arrow")) {
                e.stopPropagation();
              }
            }}
          >
            <CommandInput
              placeholder={
                page.startsWith("value:")
                  ? "Enter a value..."
                  : page === "root"
                    ? "Filter..."
                    : (activeColumn?.label ?? "Filter...")
              }
              value={page.startsWith("value:") ? draftValue : search}
              onValueChange={(value) => {
                if (page.startsWith("value:")) {
                  setDraftValue(value);
                } else {
                  setSearch(value);
                }
              }}
              // biome-ignore lint/complexity/noUselessFragments: CommandInput requires icon slot
              icon={<></>}
            />
            <CommandEmpty>No filter found</CommandEmpty>
            <CommandList>
              {page === "root" ? (
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onChange({
                        ...state,
                        includeCancelled: !state.includeCancelled,
                      });
                      onOpenChange(false);
                    }}
                  >
                    {state.includeCancelled ? (
                      <XCircleIcon />
                    ) : (
                      <CheckCircle2Icon />
                    )}
                    {state.includeCancelled
                      ? "Include cancelled results"
                      : "Active results only"}
                  </CommandItem>
                  <CommandSeparator />
                  {visibleColumns.map((column) => (
                    <CommandItem
                      key={column.field}
                      onSelect={() => openFieldPage(column, setPage)}
                    >
                      {column.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}

              {page.startsWith("enum:") && activeColumn?.enumValues ? (
                <CommandGroup heading={activeColumn.label}>
                  {activeColumn.enumValues.map((value) => {
                    const existing = state.columnFilters.find(
                      (f) => f.field === activeColumn.field,
                    );
                    const selected =
                      existing?.op === "in" &&
                      Array.isArray(existing.value) &&
                      existing.value.includes(value);

                    return (
                      <FilterCheckboxItem
                        key={value}
                        value={value}
                        checked={!!selected}
                        onCheckedChanged={(flag) =>
                          toggleEnumValue(activeColumn.field, value, flag)
                        }
                      >
                        {value}
                      </FilterCheckboxItem>
                    );
                  })}
                  <CommandItem onSelect={() => setPage("root")}>
                    Done
                  </CommandItem>
                </CommandGroup>
              ) : null}

              {page.startsWith("field:") && activeColumn?.type === "boolean" ? (
                <CommandGroup heading={activeColumn.label}>
                  <CommandItem
                    onSelect={() =>
                      applyColumnFilter({
                        field: activeColumn.field,
                        op: "eq",
                        value: true,
                      })
                    }
                  >
                    Yes
                  </CommandItem>
                  <CommandItem
                    onSelect={() =>
                      applyColumnFilter({
                        field: activeColumn.field,
                        op: "eq",
                        value: false,
                      })
                    }
                  >
                    No
                  </CommandItem>
                  <CommandItem onSelect={() => setPage("root")}>
                    Back
                  </CommandItem>
                </CommandGroup>
              ) : null}

              {page.startsWith("operators:") && activeColumn ? (
                <CommandGroup heading={activeColumn.label}>
                  {operatorsForColumnType(activeColumn.type).map((op) => (
                    <CommandItem
                      key={op}
                      onSelect={() => {
                        if (!operatorNeedsValue(op)) {
                          applyColumnFilter({
                            field: activeColumn.field,
                            op,
                          });
                          return;
                        }
                        setPage(`value:${activeColumn.field}:${op}`);
                        setDraftValue("");
                      }}
                    >
                      {operatorLabel(op)}
                    </CommandItem>
                  ))}
                  <CommandItem onSelect={() => setPage("root")}>
                    Back
                  </CommandItem>
                </CommandGroup>
              ) : null}

              {page.startsWith("value:") && activeColumn ? (
                <CommandGroup
                  heading={`${activeColumn.label} ${operatorLabel(page.split(":")[2] as FilterOp)}`}
                >
                  <CommandItem
                    onSelect={() => {
                      const op = page.split(":")[2] as FilterOp;
                      const parsed = parseFilterValue(
                        draftValue,
                        activeColumn.type,
                      );
                      if (parsed === undefined) return;
                      applyColumnFilter({
                        field: activeColumn.field,
                        op,
                        value: parsed,
                      });
                    }}
                  >
                    Apply “{draftValue || "…"}”
                  </CommandItem>
                  <CommandItem
                    onSelect={() => setPage(`operators:${activeColumn.field}`)}
                  >
                    Back
                  </CommandItem>
                </CommandGroup>
              ) : null}

              {hasFilters && page === "root" ? (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        onChange(defaultExploreFilterState());
                        onOpenChange(false);
                      }}
                    >
                      <XIcon className="size-4" />
                      Clear filters
                    </CommandItem>
                  </CommandGroup>
                </>
              ) : null}
            </CommandList>
          </Command>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

function openFieldPage(column: ColumnDef, setPage: (page: MenuPage) => void) {
  if (column.enumValues && column.enumValues.length > 0) {
    setPage(`enum:${column.field}`);
    return;
  }
  if (column.type === "boolean") {
    setPage(`field:${column.field}`);
    return;
  }
  setPage(`operators:${column.field}`);
}

function FilterCheckboxItem({
  checked,
  onCheckedChanged,
  children,
  value,
}: {
  checked: boolean;
  onCheckedChanged: (flag: boolean) => void;
  children?: ReactNode;
  value: string;
}) {
  return (
    <CommandItem
      onSelect={() => onCheckedChanged(!checked)}
      className="flex items-center gap-2"
      value={value}
    >
      <Checkbox
        isSelected={checked}
        className="border-muted-foreground"
        onChange={(flag) => onCheckedChanged(!!flag)}
        excludeFromTabOrder
      >
        {children}
      </Checkbox>
    </CommandItem>
  );
}

function operatorLabel(op: FilterOp): string {
  switch (op) {
    case "contains":
      return "contains";
    case "eq":
      return "is";
    case "startsWith":
      return "starts with";
    case "endsWith":
      return "ends with";
    case "gt":
      return "greater than";
    case "gte":
      return "at least";
    case "lt":
      return "less than";
    case "lte":
      return "at most";
    case "isNull":
      return "is empty";
    case "isNotNull":
      return "is not empty";
    default:
      return op;
  }
}

function parseFilterValue(
  raw: string,
  type: ColumnDef["type"],
): string | number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  if (type === "number") {
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return trimmed;
}
